import * as React from 'react';
import styled from '@emotion/styled';
import { Box, BoxProps } from '../box';
import { Text } from '../text';
import TextInput from './TextInput';
import Checkbox from './Checkbox';
import { Radio, RadioGroup } from './Radio';
import Select from './Select';
import { Slider } from '../slider';
import Textarea from './Textarea';
import { get, isset } from '../../utils/index';

const validateField = (required: boolean, validate: any, messages: any): any => (value: any, data: any) => {
    let error: string;

    if (required && (value === undefined || value === '')) {
        error = messages.required;
    } else if (validate) {
        if (typeof validate === 'function') {
            error = validate(value, data);
        } else if (validate.regex) {
            if (!validate.regex.test(value)) {
                error = validate.message || messages.invalid;
            }
        }
    }

    return error;
};

export interface ContentProps extends BoxProps {
    checked?: boolean
    children?: any
    className?: string
    component?: any
    context?: any
    disabled?: boolean
    error?: any
    help?: string
    htmlFor?: string
    label?: string
    name?: string
    required?: boolean
    field?: any
    theme?: any
    type?: 'input' | 'checkbox' | 'select' | 'textarea' | 'radio' | 'slider'
    validate?: any
    value?: any
    onBlur?: (e: React.FormEvent) => void
    onFocus?: (e: React.FormEvent) => void
    onChange?: (e: React.FormEvent) => void
}

const FormFieldBox = styled(Box)``;

class FormFieldContext extends React.Component<ContentProps> {

    componentDidMount() {
        const { context } = this.props;
        if (context) {
            const { name, value } = this.props;
            const { update } = context;
            if (typeof value !== 'undefined') {
                update(name, value);
            }
        }
    }

    renderChildren = (value: any, update: any) => {
        const {
            name,
            label,
            checked,
            component,
            disabled,
            required,
            field,
            theme,
            type,
            defaultValue,
            value: valueProp,
            onChange,
            onFocus,
            onBlur,
            ...rest
        } = this.props;

        delete rest.className;

        let Input = component || TextInput;
        let fieldStyles: any = {};

        const fieldProps: { [key: string]: any } = {
            name,
            defaultValue,
            required,
            disabled,
            onChange: (e: React.FormEvent<HTMLInputElement>) => {
                const event: any = e;
                update(name, event.value || event.target.value || '');
                // if (onChange) {
                //     onChange(e);
                // }
            },
            onFocus,
            onBlur
        };

        if (undefined !== type) {
            if (type === 'checkbox') {
                Input = Checkbox;
            } else if (type === 'select') {
                Input = Select;
            } else if (type === 'textarea') {
                Input = Textarea;
            } else if (type === 'radio') {
                Input = RadioGroup;
            } else if (type === 'slider') {
                Input = Slider.Container;
            }
        }

        if (Input === Checkbox) {
            return (
                <Input
                    {...fieldProps}
                    {...field}
                    label={label}
                    checked={(isset(get(value, name))) ? get(value, name) : checked || false}
                    onChange={(e: React.FormEvent<HTMLInputElement>) => {
                        const event: any = e;
                        update(name, event.target.checked);
                        if (onChange) {
                            onChange(e);
                        }
                    }}
                />
            );
        }

        const fieldValue = get(value, name);
        if (isset(fieldValue)) {
            field.value = fieldValue;
        } else if (isset(valueProp)) {
            field.value = valueProp;
        }

        if (Input === Select) {
            const selectOptions = (field && field.options) ? field.options : {};
            const inputChildren = Object.keys(selectOptions).map((k) => (
                <option value={k} key={`${name}-${k}`}>{selectOptions[k]}</option>
            ));
            return (
                <Input
                    {...fieldProps}
                    {...field}
                >
                    {inputChildren}
                </Input>
            );
        }
        if (Input === RadioGroup) {
            const radioOptions = (field && field.options) ? field.options : [];
            const inputChildren = (radioOptions || [])
                .map(({
                    value: optionValue,
                    label: optionLabel,
                    options
                }: { value?: any, label?: string, options?: any } = {}) => (
                    <Radio key={`${name}-${optionValue}`} value={optionValue} {...options}>{optionLabel}</Radio>
                ));
            return (
                <Input
                    pt={2}
                    {...fieldProps}
                    {...field}
                >
                    {inputChildren}
                </Input>
            );
        }
        if (Input === Slider.Container) {
            return (
                <Input
                    {...fieldProps}
                    {...field}
                    onChange={(sliderValue: number) => {
                        update(name, sliderValue);
                        // if (onChange) {
                        //     onChange(e);
                        // }
                    }}
                >
                    <Slider.Track />
                    <Slider.FilledTrack />
                    <Slider.Thumb />
                </Input>
            );
        }

        if (theme && theme.components) {
            if (Input === TextInput) {
                fieldStyles = theme.components.textInput;
            }
        }

        return (
            <Input
                {...fieldProps}
                {...field}
                focusIndicator={false}
                {...fieldStyles}
            />
        );
    }

    render() {
        const {
            children,
            className,
            component,
            context,
            error,
            // focus,
            help,
            htmlFor,
            label,
            name,
            // pad,
            required,
            // style,
            theme,
            validate,
            onBlur,
            onFocus,
            type,
            ...rest
        } = this.props;

        let normalizedError = error;
        let contents = children;
        const fieldStyles = theme && theme.components ? theme.components.formField : {};

        if (context) {
            const { addValidation, errors, value, update, messages } = context;
            addValidation(name, validateField(required, validate, messages));
            normalizedError = error || errors[name];
            contents = children || this.renderChildren(value, update);
        }

        return (
            <FormFieldBox {...rest}>
                {(label && type !== 'checkbox') ? (
                    <>
                        {label && (
                            <Text as="label" htmlFor={htmlFor} {...fieldStyles.label}>{label}</Text>
                        )}
                    </>
                ) : (
                    undefined
                )}
                {contents}
                {normalizedError && (
                    <Text mt="4px" {...fieldStyles.error}>{normalizedError}</Text>
                )}
                {help && (
                    <Text mt="4px" {...fieldStyles.help}>{help}</Text>
                )}
            </FormFieldBox>
        );
    }
}

export default FormFieldContext;
